/**
 * Table.tsx — Typed data table with sorting, loading, and empty states.
 *
 * Usage:
 *   <Table
 *     data={rows}
 *     columns={[
 *       { key: 'census_id', header: 'Census ID', mono: true, render: r => <Link>{r.census_id}</Link> },
 *       { key: 'name', header: 'Outlet Name' },
 *       { key: 'confidence', header: 'Confidence', align: 'right', render: r => <ConfidenceTag score={r.confidence} /> },
 *     ]}
 *     loading={loading}
 *     emptyState={<EmptyState title="No results" description="Try adjusting filters" />}
 *     onRowClick={row => navigate(`/outlet/${row.census_id}`)}
 *   />
 *
 * Features:
 *   - Zebra striping (subtle)
 *   - Hover state on rows
 *   - Sticky header
 *   - Sortable columns (optional)
 *   - Loading skeleton
 *   - Empty state slot
 *   - Row click handler
 *   - Column alignment (left | right | center)
 *   - Monospace cells
 *   - Horizontal scroll on mobile
 */

import { ReactNode, useState } from 'react';
import { theme } from '../../theme';
import { Icon } from './Icons';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T, index: number) => ReactNode;
  accessor?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  mono?: boolean;
  sortable?: boolean;
  nowrap?: boolean;
}

export interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  loadingRows?: number;
  emptyState?: ReactNode;
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T, index: number) => string | number;
  minWidth?: number;
  compact?: boolean;
  // Controlled-sort mode: pass sortKey + sortDir + onSort and the table
  // surrenders sort state to the parent (typically used with server-side
  // sorting + pagination). Uncontrolled mode (no onSort) is unchanged.
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

export function Table<T>({
  data,
  columns,
  loading,
  loadingRows = 8,
  emptyState,
  onRowClick,
  rowKey = (_, i) => i,
  minWidth,
  compact = false,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  onSort,
}: TableProps<T>) {
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>('asc');

  const isControlled = onSort !== undefined;
  const sortKey = isControlled ? sortKeyProp ?? null : internalSortKey;
  const sortDir = isControlled ? sortDirProp ?? 'asc' : internalSortDir;

  const handleSort = (col: Column<T>) => {
    if (!col.sortable) return;
    const newDir: 'asc' | 'desc' =
      sortKey === col.key && sortDir === 'asc' ? 'desc' : 'asc';
    if (isControlled) {
      onSort!(col.key, newDir);
    } else {
      setInternalSortKey(col.key);
      setInternalSortDir(newDir);
    }
  };

  const cellPadY = compact ? '8px' : '12px';
  const cellPadX = compact ? '10px' : '14px';

  return (
    <div style={{
      border: `1px solid ${theme.borderColor.default}`,
      borderRadius: theme.radius.lg,
      overflow: 'hidden',
      background: theme.surface.panel,
    }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{
          width: '100%',
          minWidth,
          borderCollapse: 'collapse',
          fontSize: theme.fontSize.base,
        }}>
          <thead>
            <tr style={{ background: theme.surface.card }}>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  style={{
                    padding: `10px ${cellPadX}`,
                    color: theme.text.tertiary,
                    fontSize: theme.fontSize.xs,
                    fontWeight: theme.fontWeight.semibold,
                    letterSpacing: theme.letterSpacing.wide,
                    textTransform: 'uppercase',
                    textAlign: col.align || 'left',
                    borderBottom: `1px solid ${theme.borderColor.default}`,
                    whiteSpace: 'nowrap',
                    width: col.width,
                    cursor: col.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    fontFamily: theme.font.ui,
                  }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                  }}>
                    {col.header}
                    {col.sortable && sortKey === col.key && (
                      sortDir === 'asc' ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  {columns.map((col, j) => (
                    <td key={col.key} style={{ padding: `${cellPadY} ${cellPadX}`, borderBottom: `1px solid ${theme.borderColor.subtle}` }}>
                      <div style={{
                        height: 12,
                        borderRadius: 4,
                        background: 'rgba(255,255,255,0.05)',
                        width: j === 0 ? '60%' : j === columns.length - 1 ? '40%' : '80%',
                      }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  {emptyState ?? (
                    <div style={{
                      padding: `${theme.space[12]} ${theme.space[6]}`,
                      textAlign: 'center',
                      color: theme.text.tertiary,
                      fontSize: theme.fontSize.base,
                    }}>
                      No results
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <TableRow
                  key={rowKey(row, i)}
                  row={row}
                  index={i}
                  columns={columns}
                  cellPadY={cellPadY}
                  cellPadX={cellPadX}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TableRow<T>({
  row, index, columns, cellPadY, cellPadX, onRowClick,
}: {
  row: T;
  index: number;
  columns: Column<T>[];
  cellPadY: string;
  cellPadX: string;
  onRowClick?: (row: T, i: number) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      style={{
        background: hover
          ? theme.surface.cardHover
          : index % 2 === 1 ? 'rgba(15, 22, 40, 0.4)' : 'transparent',
        cursor: onRowClick ? 'pointer' : 'default',
        borderBottom: `1px solid ${theme.borderColor.subtle}`,
        transition: `background ${theme.motion.fast}`,
      }}
    >
      {columns.map(col => {
        const content = col.render
          ? col.render(row, index)
          : col.accessor
          ? col.accessor(row)
          : (row as Record<string, ReactNode>)[col.key];
        return (
          <td
            key={col.key}
            style={{
              padding: `${cellPadY} ${cellPadX}`,
              color: theme.text.primary,
              fontSize: theme.fontSize.base,
              textAlign: col.align || 'left',
              fontFamily: col.mono ? theme.font.data : theme.font.ui,
              whiteSpace: col.nowrap ? 'nowrap' : 'normal',
              fontVariantNumeric: col.mono ? 'tabular-nums' : 'normal',
            }}
          >
            {content ?? <span style={{ color: theme.text.quaternary }}>—</span>}
          </td>
        );
      })}
    </tr>
  );
}

/* ═══════════════ TABLE TOOLBAR ═══════════════ */
/* Optional toolbar row above the table for "Showing X of Y" + pagination controls. */

export function TableToolbar({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.space[3],
      marginBottom: theme.space[3],
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════ PAGINATION ═══════════════ */

export function Pagination({
  page, totalPages, onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <PageButton disabled={page <= 1} onClick={() => onChange(Math.max(1, page - 1))}>
        <Icon.ChevronLeft size={12} /> Prev
      </PageButton>
      <span style={{ fontSize: theme.fontSize.sm, color: theme.text.tertiary, padding: '0 8px' }}>
        Page <span style={{ color: theme.text.primary, fontFamily: theme.font.data }}>{page}</span> of <span style={{ fontFamily: theme.font.data }}>{totalPages}</span>
      </span>
      <PageButton disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))}>
        Next <Icon.ChevronRight size={12} />
      </PageButton>
    </div>
  );
}

function PageButton({ children, disabled, onClick }: { children: ReactNode; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: theme.surface.card,
        border: `1px solid ${theme.borderColor.default}`,
        color: theme.text.primary,
        borderRadius: theme.radius.sm,
        padding: '5px 10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontSize: theme.fontSize.sm,
        fontFamily: theme.font.ui,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        transition: `all ${theme.motion.fast}`,
      }}
    >
      {children}
    </button>
  );
}
